/**
 * @architecture-header 1
 * @layer routes
 * @domain workspace-shell
 * @responsibility Render the main column header, breadcrumb and per-section screen.
 * @state-owner navigation store; dataset store
 * @allowed-imports React; workspace and dataset stores; shared components; module public APIs only
 * @forbidden in-product role switching; direct API client
 * @public-api WorkspaceShell
 * @side-effects navigation only
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import type { ReactNode } from 'react';
import { useActiveRole } from '../../app/providers/ActiveRoleProvider';
import {
  ActivityBoard,
  ActivityEditor,
  ActivityParsing,
  StudentActivityPlayer,
  StudentBoard,
  StudentReview,
  UploadButton,
} from '../../modules/activities';
import {
  ClassDetail,
  GuardianInvitePage,
  RunProgress,
  StudentClassHome,
  StudentPage,
} from '../../modules/classrooms';
import { InterventionPage, RunDashboard } from '../../modules/live';
import { AnswerView, LearningReport, RunReview, StudentRecap } from '../../modules/reports';
import { MainEmpty } from '../../shared/components/ListColumn';
import { Breadcrumb, type CrumbPart } from '../../shared/components/PageShell';
import { ChevronLeftIcon } from '../../shared/components/VispoIcon';
import { currentClassIndex, findActivity, findRun } from '../../shared/lib/selectors';
import { useDatasetStore } from '../../shared/model/dataset-store';
import { useTranslator } from '../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../shared/model/workspace-store';

interface MainContent {
  readonly crumb: readonly (CrumbPart | null)[];
  readonly screen: ReactNode;
}

export function MainColumn() {
  const t = useTranslator();
  const role = useActiveRole();
  const teacher = role === 'teacher';
  const section = useWorkspaceUiStore((state) => state.section);
  const selection = useWorkspaceUiStore((state) => state.selection);
  const open = useWorkspaceUiStore((state) => state.open);
  const go = useWorkspaceUiStore((state) => state.go);
  const navigate = useWorkspaceUiStore((state) => state.navigate);
  const closeItem = useWorkspaceUiStore((state) => state.closeItem);
  const openItem = useWorkspaceUiStore((state) => state.openItem);
  const closeMobileMain = useWorkspaceUiStore((state) => state.closeMobileMain);
  const mobileMainOpen = useWorkspaceUiStore((state) => state.mobileMainOpen);
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const runs = useDatasetStore((state) => state.runs);

  const content = ((): MainContent => {
    if (teacher && section === 'act') {
      const activity = findActivity(activities, selection);
      if (!activity) return { crumb: [{ label: t('活動') }], screen: <ActivityBoard /> };
      return {
        crumb: [
          { label: t('活動'), onSelect: () => go('act', null) },
          activity.classIndex != null
            ? {
                label: classes[activity.classIndex].name,
                onSelect: () => navigate('room', `c${activity.classIndex}`),
              }
            : null,
          { label: activity.name },
        ],
        screen:
          activity.status === 'parsing' ? (
            <ActivityParsing activity={activity} />
          ) : (
            <ActivityEditor activity={activity} />
          ),
      };
    }

    if (teacher && section === 'room') {
      const classIndex = currentClassIndex(selection);
      const classroom = classes[classIndex];
      const base: CrumbPart = { label: t('班級'), onSelect: () => go('room', null) };
      if (open?.k === 'stu') {
        return {
          crumb: [base, { label: classroom.name, onSelect: closeItem }, { label: open.n ?? '' }],
          screen: <StudentPage classIndex={classIndex} student={open.n ?? ''} />,
        };
      }
      if (open?.k === 'mat') {
        const material = findActivity(activities, open.id ?? null);
        if (material) {
          return {
            crumb: [base, { label: classroom.name, onSelect: closeItem }, { label: material.name }],
            screen:
              material.status === 'parsing' ? (
                <ActivityParsing activity={material} />
              ) : (
                <ActivityEditor activity={material} />
              ),
          };
        }
      }
      if (open?.k === 'minv') {
        return { crumb: [base, { label: t('邀請我的家長') }], screen: <GuardianInvitePage /> };
      }
      if (open?.k === 'ans') {
        return {
          crumb: [
            base,
            { label: classroom.name, onSelect: closeItem },
            // One answer sheet is opened from the review queue, so the crumb leads back to it.
            { label: t('回顧'), onSelect: () => openItem({ k: 'rev', id: open.id, stu: null }) },
            { label: open.stu ?? '' },
          ],
          screen: <AnswerView runId={open.id ?? ''} student={open.stu ?? ''} />,
        };
      }
      if (open?.k === 'rev') {
        return {
          crumb: [base, { label: classroom.name, onSelect: closeItem }, { label: t('回顧') }],
          screen: <RunReview runId={open.id ?? ''} />,
        };
      }
      if (open?.k === 'prog') {
        return {
          crumb: [base, { label: classroom.name, onSelect: closeItem }, { label: t('作答進度') }],
          screen: <RunProgress runId={open.id ?? ''} />,
        };
      }
      return {
        crumb: [base, { label: classroom.name }],
        screen: <ClassDetail classIndex={classIndex} />,
      };
    }

    if (teacher && section === 'live') {
      const run = findRun(runs, selection);
      if (!run) {
        return {
          crumb: [{ label: t('即時') }],
          screen: (
            <MainEmpty
              description={t('到班級的活動分頁發一份課堂進行。')}
              title={t('現在沒有課在上')}
            />
          ),
        };
      }
      const base: CrumbPart[] = [
        { label: t('即時'), onSelect: () => go('live', null) },
        {
          label: classes[run.classIndex].name,
          onSelect: () => navigate('room', `c${run.classIndex}`),
        },
      ];
      if (open?.k === 'iv') {
        return {
          crumb: [
            ...base,
            { label: findActivity(activities, run.activityId)?.name ?? '', onSelect: closeItem },
            { label: open.n ?? '' },
          ],
          screen: <InterventionPage run={run} student={open.n ?? ''} />,
        };
      }
      return {
        crumb: [...base, { label: findActivity(activities, run.activityId)?.name ?? '' }],
        screen: <RunDashboard run={run} />,
      };
    }

    if (teacher) {
      if (!selection) {
        return {
          crumb: [{ label: t('報告') }],
          screen: <MainEmpty description={t('左邊挑一位學生。')} title={t('學習報告')} />,
        };
      }
      const classroom = classes.find((item) => item.students.includes(selection));
      return {
        crumb: [
          { label: t('報告'), onSelect: () => go('rep', null) },
          classroom
            ? {
                label: classroom.name,
                onSelect: () => navigate('room', `c${classes.indexOf(classroom)}`),
              }
            : null,
          { label: selection },
        ],
        screen: <LearningReport student={selection} />,
      };
    }

    if (section === 'room') {
      const classIndex = currentClassIndex(selection);
      const base: CrumbPart = { label: t('我的班級'), onSelect: () => go('room', null) };
      if (open?.k === 'minv') {
        return {
          crumb: [
            base,
            { label: classes[classIndex].name, onSelect: closeItem },
            { label: t('邀請我的家長') },
          ],
          screen: <GuardianInvitePage />,
        };
      }
      return {
        crumb: [base, { label: classes[classIndex].name }],
        screen: <StudentClassHome classIndex={classIndex} />,
      };
    }

    if (section === 'act') {
      const run = findRun(runs, selection);
      if (!run) return { crumb: [{ label: t('我的作業') }], screen: <StudentBoard /> };
      return {
        crumb: [
          { label: t('我的作業'), onSelect: () => go('act', null) },
          {
            label: classes[run.classIndex].name,
            onSelect: () => navigate('room', `c${run.classIndex}`),
          },
          { label: findActivity(activities, run.activityId)?.name ?? '' },
        ],
        screen:
          run.status === 'ended' ? (
            <StudentReview run={run} />
          ) : (
            <StudentActivityPlayer run={run} />
          ),
      };
    }

    const run = findRun(runs, selection);
    if (!run) {
      return {
        crumb: [{ label: t('我的報告') }],
        screen: <MainEmpty description={t('左邊挑一次上課。')} title={t('我的報告')} />,
      };
    }
    return {
      crumb: [
        { label: t('我的報告'), onSelect: () => go('rep', null) },
        {
          label: classes[run.classIndex].name,
          onSelect: () => navigate('room', `c${run.classIndex}`),
        },
        { label: findActivity(activities, run.activityId)?.name ?? '' },
      ],
      screen: <StudentRecap run={run} />,
    };
  })();

  return (
    <main className={`main${mobileMainOpen ? ' open' : ''}`} id="main">
      <header className="top">
        <button className="mback" type="button" onClick={closeMobileMain}>
          <ChevronLeftIcon />
        </button>
        <div style={{ minWidth: 0 }}>
          <div className="ttl" id="ttl">
            <Breadcrumb parts={content.crumb} />
          </div>
          <div className="sub2" id="sub2" />
        </div>
        <div className="sp" />
        {teacher && section === 'act' ? <UploadButton /> : null}
      </header>
      <div className="body" id="body">
        {content.screen}
      </div>
    </main>
  );
}
